import { ComponentProps, CSSProperties, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { colors, space } from '@ardoq/design-tokens';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { UnstyledLink } from './UnstyledLink';
import { OpenInNewIcon } from '@ardoq/icons';

type LinkBaseProps = Omit<
  ComponentProps<typeof UnstyledLink>,
  'target' | 'rel'
> & {
  href: string;
  children: ReactNode;
};

export type InlineLinkProps = LinkBaseProps &
  (
    | { target?: '_self'; opensInNewTabLabel?: never }
    | { target: '_blank'; opensInNewTabLabel: string }
  );

export type InternalLinkProps = LinkBaseProps;

export type ExternalLinkProps = LinkBaseProps & {
  opensInNewTabLabel: string;
};

/**
 * Overrides the colour of the link text, its underline and the external icon.
 * Only status notifications set it.
 */
export const LINK_COLOR_CSS_VARIABLE = '--link-color';

const linkUnderline = css`
  text-decoration: underline;
  text-decoration-color: var(
    ${LINK_COLOR_CSS_VARIABLE},
    ${colors.textModerate}
  );
  text-decoration-thickness: 0.04em;
  text-underline-offset: 0.2em;
`;

const StyledLink = styled(UnstyledLink)`
  color: var(${LINK_COLOR_CSS_VARIABLE}, ${colors.textDefault});
  ${linkUnderline};
`;

/** For a call site with no locale in scope. Prefer a translated string. */
export const OPENS_IN_NEW_TAB_LABEL = '(new tab)';
const UnderlinedLabel = styled.span`
  ${linkUnderline};
`;

const ExternalIcon = styled(OpenInNewIcon)`
  margin-left: ${space.xsmall};
  vertical-align: middle;
  color: var(${LINK_COLOR_CSS_VARIABLE}, ${colors.icon});
`;

/** `Icon` sets its size as an inline style, so `style` is the only way in. */
const externalIconSize: CSSProperties = {
  width: '1em',
  height: '1em',
  minWidth: '1em',
};

const StyledExternalLink = styled(StyledLink)`
  text-decoration: none;
`;

/**
 * Link inside a sentence. Carries no icon: between words an icon interrupts
 * reading. `target="_blank"` opens a new tab, sets `rel="noopener"` and ends
 * the link text with `opensInNewTabLabel`, which the screen reader reads as
 * written.
 *
 * Takes its typography from the surrounding text, so wrap it in the typography
 * component that sets the size — `<InlineText variant="text1">`,
 * `<Text variant="text2">`, `<Paragraph variant="caption">`. `text1`, `text2`
 * and `caption` are the supported sizes; header typography is not, because a
 * link is not a heading.
 *
 * Do not fix the size with `styled(InlineLink)` — the size then contradicts
 * whatever text the link sits in, and it is invisible at the call site.
 * `ardoq-lint/no-styled-link` reports it.
 */
export const InlineLink = (props: InlineLinkProps) => {
  if (props.target !== '_blank') {
    return <StyledLink {...props} target="_self" />;
  }
  const { children, opensInNewTabLabel, target, ...rest } = props;
  return (
    <StyledLink {...rest} target={target} rel="noopener">
      {children} {opensInNewTabLabel}
    </StyledLink>
  );
};

/**
 * Standalone link to a destination inside Ardoq. Opens in the same tab and
 * carries no icon. Sits on its own or directly after the content it relates
 * to; for a link inside a sentence use {@link InlineLink}.
 *
 * Takes its typography from the surrounding text; see {@link InlineLink} for
 * the supported sizes and how to wrap it.
 */
export const InternalLink = (props: InternalLinkProps) => (
  <StyledLink {...props} target="_self" />
);

/**
 * Standalone link to a destination outside the app, labelled with a trailing
 * open-in-new-tab icon that scales with the inherited typography. Always
 * opens a new tab, so it is never used inside a sentence.
 *
 * Takes its typography from the surrounding text; see {@link InlineLink} for
 * the supported sizes and how to wrap it.
 */
export const ExternalLink = ({
  children,
  opensInNewTabLabel,
  ...props
}: ExternalLinkProps) => (
  <StyledExternalLink {...props} target="_blank" rel="noopener">
    <UnderlinedLabel>{children}</UnderlinedLabel>
    <VisuallyHidden elementType="span">{` ${opensInNewTabLabel}`}</VisuallyHidden>
    <ExternalIcon style={externalIconSize} />
  </StyledExternalLink>
);
